import type { ReactElement, ReactNode } from 'react';
import { TreeItem } from '@mui/x-tree-view';

import { OrgUnitSelectionStatus } from 'src/constants/orgUnit';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { FilterGroups } from 'src/components/app/context/FilterContext';
import ChildrenLoadingIndicator from 'src/components/common/OrgTree/components/ChildrenLoadingIndicator';
import type {
  NodeObject,
  OrgTreeState,
  OrgUnitMap,
  TreeItemChild,
} from 'src/components/common/OrgTree/types';

import { styles } from './index.styles';

export const hasSingleLeafUnit = (
  organisationalUnits?: ModuleOrPlanActionOrgUnit[],
) => {
  if (!organisationalUnits || organisationalUnits.length !== 1) return false;

  return organisationalUnits[0].is_leaf;
};

/**
 * This function gets an 'orgUnit' and an 'orgTree' and returns the parent org unit of the orgUnit passed in.
 * If there is no parent orgUnit the function returns the first orgUnit of the passed orgTree.
 */
export const getParentOrgUnit = (
  orgUnit: ModuleOrPlanActionOrgUnit,
  orgTreeStateNodes: NodeObject[],
): ModuleOrPlanActionOrgUnit => {
  const orgUnitId = orgUnit.id;
  let parentOrgUnit;

  // If the passed orgUnit is the root orgUnit return the root orgUnit.
  if (
    (orgTreeStateNodes[0]?.label as ReactElement)?.props.orgUnit
      ?.org_unit_id === orgUnit.org_unit_id
  ) {
    return orgUnit;
  }

  for (let i = 0; i < orgTreeStateNodes.length; i++) {
    const orgUnitInOrgTree = orgTreeStateNodes[i];

    if (orgUnitInOrgTree.children) {
      //  Checks if the passed orgUnitId is contained in a childOrgUnit of the passed orgTree and returns true if yes.
      const isCurrentOrgUnit = orgUnitInOrgTree.children.some(
        (childOrgUnit: any) => {
          if (childOrgUnit.label.props.orgUnit) {
            return childOrgUnit.label.props.orgUnit.id === orgUnitId;
          }
          return false;
        },
      );

      if (isCurrentOrgUnit) {
        parentOrgUnit =
          (orgUnitInOrgTree?.label as ReactElement)?.props.orgUnit || 1;

        return parentOrgUnit;
      }

      if (getParentOrgUnit(orgUnit, orgUnitInOrgTree.children)) {
        parentOrgUnit = getParentOrgUnit(orgUnit, orgUnitInOrgTree.children);
      }
    }
  }

  return (
    parentOrgUnit ||
    (orgTreeStateNodes[0]?.label as ReactElement)?.props.orgUnit
  );
};

export const orgUnitToNode =
  (
    itemsAreCheckable?: boolean,
    fetchChildren?: (arg1: any) => Promise<void>,
    createLabel?: (arg1: ModuleOrPlanActionOrgUnit) => ReactElement,
  ) =>
  (orgUnit: ModuleOrPlanActionOrgUnit): NodeObject => {
    const disabled =
      'selection_status' in orgUnit &&
      (orgUnit.selection_status ===
        OrgUnitSelectionStatus.LESS_THAN_MIN_RESPONDENT_AMOUNT ||
        orgUnit.selection_status === OrgUnitSelectionStatus.NEGATIVE_SELECTION);

    const isChildless =
      orgUnit.is_leaf ||
      ('include_subnodes' in orgUnit && !orgUnit.include_subnodes);

    const children: NodeObject[] = isChildless
      ? []
      : [
          {
            label: (
              <ChildrenLoadingIndicator
                onMount={() => fetchChildren && fetchChildren(orgUnit.id)}
              />
            ),
            value: -orgUnit.id,
            disabled: true,
            showCheckbox: false,
            icon: <div />,
          },
        ];

    return {
      label: createLabel ? createLabel(orgUnit) : orgUnit.name,
      value: orgUnit.id,
      showCheckbox: !!itemsAreCheckable,
      disabled,
      children,
    };
  };

/**
 * Add children to node
 */
export const addChildrenToNode = (
  children: any,
  orgUnitId: any,
  converter: any,
  nodes: any[] | undefined,
): any =>
  nodes?.map((node: any) => {
    if (node.value === orgUnitId) {
      return {
        ...node,
        children: children.map(converter),
      };
    }

    if (!('children' in node)) return node;

    return {
      ...node,
      children: addChildrenToNode(
        children,
        orgUnitId,
        converter,
        node.children,
      ),
    };
  });

/**
 * OrgUnit to map entry.
 */
export const orgUnitToMapEntry = (orgUnit: ModuleOrPlanActionOrgUnit) => ({
  [orgUnit.id]: { orgUnit, childrenRequested: false, childrenLoaded: false },
});

/**
 * Helper function to create a filter-context compatible FilterValue from an orgUnit.
 */
export const orgUnitToFilter = (orgUnit: ModuleOrPlanActionOrgUnit) => ({
  label: `${orgUnit.name} ${orgUnit.org_unit_id}`,
  filterExpr: {
    orgUnit: orgUnit.org_unit_id,
    orgUnitDbId: `${orgUnit.id}`,
  },
});

export const getMockOrgTreeState = (state: any): OrgTreeState => ({
  nodes:
    state && state.initialOrgUnits
      ? state.initialOrgUnits.map(orgUnitToNode())
      : [],
  orgUnitMap:
    state && state.initialOrgUnits
      ? Object.assign({}, ...state.initialOrgUnits.map(orgUnitToMapEntry))
      : {},
  selectedOrgUnits: [],
  setLatestSelectedOrgUnit: vi.fn(),
  setNodes: vi.fn(),
  setSelectedOrgUnits: vi.fn(),
  latestSelectedOrgUnit: undefined,
  setOrgUnitMap: vi.fn(),
  ...state,
});

export const createEmptyOrgTreeState = (): OrgTreeState => ({
  nodes: [],
  orgUnitMap: {},
  selectedOrgUnits: [],
  setLatestSelectedOrgUnit: () => {},
  setNodes: () => {},
  setSelectedOrgUnits: () => {},
  latestSelectedOrgUnit: undefined,
  setOrgUnitMap: () => {},
});

export const renderTreeItem = (
  value: string | number,
  label: string | ReactNode,
  children: TreeItemChild[] | NodeObject[] | undefined,
  i: number,
) => (
  <TreeItem
    key={`${value}-${i}`}
    itemId={String(value)}
    label={label}
    className={`org-tree-item-${value}`}
    sx={styles.treeItem}
    slotProps={{}}
  >
    {children?.map(({ value, label, children }, i) =>
      renderTreeItem(value, label, children, i),
    )}
  </TreeItem>
);

export const getInitialOrgUnits = (
  filterGroups: FilterGroups,
  orgUnitMap: OrgUnitMap,
) => {
  const { orgUnit: orgUnits } = filterGroups;
  if (orgUnits && orgUnits.length && Object.keys(orgUnitMap).length) {
    return orgUnits.reduce(
      (acc: ModuleOrPlanActionOrgUnit[], { filterExpr }) => {
        const mappedOrgUnit = orgUnitMap[String(filterExpr.orgUnitDbId)];
        if (mappedOrgUnit) {
          return [...acc, mappedOrgUnit.orgUnit];
        }
        return acc;
      },
      [],
    );
  }
  return [];
};
